// Global chunk loading error handler with retry mechanism
import { ChunkLoadDiagnostics } from './chunkLoadDiagnostics'

export class ChunkLoadErrorHandler {
  private static retryAttempts = new Map<string, number>()
  private static readonly MAX_RETRIES = 3
  private static readonly RETRY_DELAY = 1000
  private static isReloadDialogShown = false
  private static reloadTimeoutId: NodeJS.Timeout | null = null

  static init() {
    // Handle dynamic import failures globally
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason
      if (this.isChunkLoadError(error)) {
        event.preventDefault()
        this.handleChunkLoadError(error)
      }
    })

    // Handle script loading errors
    window.addEventListener(
      'error',
      (event) => {
        if (
          event.target &&
          (event.target as HTMLScriptElement).tagName === 'SCRIPT'
        ) {
          const scriptSrc = (event.target as HTMLScriptElement).src
          if (scriptSrc.includes('_next/static/chunks/')) {
            this.handleScriptLoadError(scriptSrc)
          }
        }
      },
      true
    )
  }

  private static isChunkLoadError(error: any): boolean {
    return (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Loading CSS chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module')
    )
  }

  private static async handleChunkLoadError(error: any) {
    const chunkName = this.extractChunkName(error)
    const attempts = this.retryAttempts.get(chunkName) || 0

    if (attempts < this.MAX_RETRIES) {
      this.retryAttempts.set(chunkName, attempts + 1)

      // Log retry attempt
      console.warn(
        `Chunk load failed for ${chunkName}, retrying (${attempts + 1}/${
          this.MAX_RETRIES
        })...`
      )

      // Add delay before retry
      await new Promise((resolve) =>
        setTimeout(resolve, this.RETRY_DELAY * (attempts + 1))
      )

      // Attempt to reload the page to get fresh chunks
      if (attempts === this.MAX_RETRIES - 1) {
        this.forcePageReload('Multiple chunk load failures detected')
      }
    } else {
      // Max retries reached, force reload
      this.forcePageReload(
        `Failed to load chunk ${chunkName} after ${this.MAX_RETRIES} attempts`
      )
    }
  }

  private static handleScriptLoadError(scriptSrc: string) {
    console.error(`Failed to load script: ${scriptSrc}`)

    // Extract chunk name from script src
    const chunkMatch = scriptSrc.match(/chunks\/(.+?)\.js/)
    const chunkName = chunkMatch ? chunkMatch[1] : 'unknown'

    const attempts = this.retryAttempts.get(chunkName as string) || 0

    if (attempts < this.MAX_RETRIES) {
      this.retryAttempts.set(chunkName as string, attempts + 1)

      // Create new script element with cache busting
      setTimeout(() => {
        const newScript = document.createElement('script')
        const cacheBuster = `?v=${Date.now()}`
        newScript.src = scriptSrc + cacheBuster
        newScript.onload = () => {
          console.log(`Successfully reloaded chunk: ${chunkName}`)
          this.retryAttempts.delete(chunkName as string)
        }
        newScript.onerror = () => {
          this.handleScriptLoadError(scriptSrc)
        }
        document.head.appendChild(newScript)
      }, this.RETRY_DELAY * attempts)
    } else {
      this.forcePageReload(`Script load failed: ${chunkName}`)
    }
  }

  private static extractChunkName(error: any): string {
    const message = error?.message || error?.toString() || ''
    const chunkMatch =
      message.match(/Loading chunk (\d+)/) ||
      message.match(/chunk-(\w+)/) ||
      message.match(/(\w+)\.chunk\.js/)
    return chunkMatch ? chunkMatch[1] : 'unknown'
  }

  private static forcePageReload(reason: string) {
    console.warn(`Requesting page reload: ${reason}`)

    // Prevent multiple dialogs from showing simultaneously
    if (this.isReloadDialogShown) {
      console.log('Reload dialog already shown, skipping duplicate request')
      return
    }

    // Debounce multiple reload requests
    if (this.reloadTimeoutId) {
      clearTimeout(this.reloadTimeoutId)
    }

    this.reloadTimeoutId = setTimeout(() => {
      this.isReloadDialogShown = true

      // Ask user for confirmation before reloading
      const userConfirmed = confirm(
        'We encountered a loading issue and need to refresh the page to continue. ' +
          'Would you like to refresh now?'
      )

      if (userConfirmed) {
        this.performReload()
      } else {
        console.log(
          'User declined page reload - chunk loading issues may persist'
        )

        // Collect diagnostic information to help understand the root cause
        ChunkLoadDiagnostics.collectDiagnosticInfo()

        // Reset the flag so user can be asked again later if needed
        this.isReloadDialogShown = false
      }
    }, 500) // Wait 500ms to collect multiple errors before showing dialog
  }

  private static performReload() {
    // Clear any cached chunks before reload
    if ('caches' in window) {
      caches
        .keys()
        .then((names) => {
          const deletions = names
            .filter(
              (name) => name.includes('next-static') || name.includes('chunks')
            )
            .map((name) => caches.delete(name))
          return Promise.all(deletions)
        })
        .finally(() => {
          location.reload()
        })
    } else {
      location.reload()
    }
  }
}
